import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Linkedin, Mail, Phone, MapPin, Send } from 'lucide-react';
import { footerLinks } from '../../data/mock';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 3000);
    }
  };

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-4">
            <Link to="/" className="flex items-center space-x-3 mb-6">
              <div className="relative">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-600 via-teal-500 to-cyan-400 rounded-2xl flex items-center justify-center shadow-xl">
                  <div className="flex flex-col items-center">
                    <span className="text-white font-extrabold text-xl leading-none">S</span>
                    <div className="flex space-x-0.5 mt-0.5">
                      <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></span>
                      <span className="w-1.5 h-1.5 bg-orange-400 rounded-full"></span>
                      <span className="w-1.5 h-1.5 bg-pink-400 rounded-full"></span>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-gradient-to-br from-orange-400 to-pink-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-[8px] font-bold">✦</span>
                </div>
              </div>
              <div className="flex flex-col">
                <span className="font-extrabold text-2xl text-white">STEAM<span className="text-teal-400">-IE</span></span>
                <span className="text-[10px] text-gray-400 font-medium tracking-widest">IGNITE MINDS • INSPIRE FUTURES</span>
              </div>
            </Link>
            <p className="text-gray-400 mb-6 leading-relaxed text-sm">
              India&apos;s first credentialing company for STEAM Education, Innovation & Entrepreneurship. 
              Empowering the next generation of innovators and leaders since 2008.
            </p>
            
            {/* Newsletter */}
            <div className="mb-6">
              <h4 className="text-white font-semibold mb-3 text-sm">Subscribe to Newsletter</h4>
              <form onSubmit={handleSubscribe} className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 flex-1 text-sm"
                />
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700 px-4">
                  <Send size={16} />
                </Button>
              </form>
              {subscribed && (
                <p className="text-green-400 text-sm mt-2">Thanks for subscribing!</p>
              )}
            </div>

            {/* Social Links */}
            <div className="flex space-x-3">
              {[
                { icon: Facebook, href: 'https://www.facebook.com/steamieeducation', label: 'Facebook' },
                { icon: Instagram, href: 'https://www.instagram.com/steamieeducation/', label: 'Instagram' },
                { icon: Linkedin, href: 'https://www.linkedin.com/company/steam-ie/posts/', label: 'LinkedIn' }
              ].map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors duration-300"
                  aria-label={label}
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links - Company */}
          <div className="lg:col-span-2">
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-white transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Offerings */}
          <div className="lg:col-span-2">
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Offerings</h4>
            <ul className="space-y-3">
              {footerLinks.offerings.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-white transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-4">
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <MapPin size={16} className="text-blue-500 mt-1 flex-shrink-0" />
                <span className="text-gray-400 text-sm">#101,102 ARG Shopping Complex, Jaipur Road, Ajmer, Rajasthan 305001</span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone size={16} className="text-blue-500 flex-shrink-0" />
                <a href="tel:+917996697777" className="text-gray-400 hover:text-white text-sm transition-colors">+91 79966 97777</a>
              </li>
              <li className="flex items-center space-x-3">
                <Mail size={16} className="text-blue-500 flex-shrink-0" />
                <a href="mailto:info@steamie.net" className="text-gray-400 hover:text-white text-sm transition-colors">info@steamie.net</a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} STEAM-IE. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6">
              {footerLinks.legal.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="text-gray-500 hover:text-gray-300 text-xs transition-colors"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
